import { changeUserGroupNotifications, createGroup, deleteGroup, fetchUserGroupById, fetchUserGroupNotifications, fetchUserGroupPagedData, updateGroup } from "@/lib/services/userGroup"
import { ResourceType } from "@/lib/types/resourceType"
import { User } from "@/lib/types/user"
import { UserGroup, UserGroupDTO, UserGroupNotification, UserGroupNotificationDTO } from "@/lib/types/userGroup"
import { defineStore } from "pinia"

interface UserGroupsState {
    userGroups: UserGroup[],
    userGroupNotifications: Record<string, Record<string, UserGroupNotification>>, // UserGroupId -> ResourceTypeId -> Notification
    loading: boolean
}

export const useUserGroupsStore = defineStore('userGroups', {
    state: (): UserGroupsState => ({
        userGroups: [],
        userGroupNotifications: {},
        loading: true,
    }),

    actions: {
        async fetchPagedData(page: number = 1, pageSize: number, sortField?: string, sortOrder?: number, filters?: Record<string, any>): Promise<PagedResultMetadata> {
            this.loading = true

            try {
                const params: PagedRequestParams = {
                    offset: (page - 1) * pageSize,
                    limit: pageSize,
                    sortField: sortField,
                    sortOrder: sortOrder && sortOrder === 1 ? 'ASC' : 'DESC',
                    filters: filters
                }

                const res = await fetchUserGroupPagedData(params)

                this.userGroups = res.items
                this.userGroups.forEach(ug => { this.fetchUserGroupNotifications(ug.id) })
                return res as PagedResultMetadata
            } catch (err) {
                console.error(err)
                throw err
            } finally {
                this.loading = false
            }
        },

        async fetchById(id: string): Promise<ErrorMessage | UserGroup> {
            this.loading = true
            try {
                const res = await fetchUserGroupById(id)
                return res
            } catch (err) {
                console.error(err)
                throw err
            } finally {
                this.loading = false
            }
        },

        async createGroup(data: UserGroup): Promise<ErrorMessage | UserGroup> {
            this.loading = true
            try {
                const res = await createGroup(data)

                if (res && 'errorCode' in res) {
                    return res
                }

                this.userGroups.push(res)
                return res
            } catch (err) {
                console.error(err)
                throw err;
            } finally {
                this.loading = false
            }
        },

        async updateGroup(data: UserGroup): Promise<ErrorMessage | UserGroup> {
            this.loading = true
            try {
                const res = await updateGroup(data)

                if (res && 'errorCode' in res) {
                    return res
                }

                const idx = this.userGroups.findIndex(g => g.id === data.id)
                if (idx !== -1) {
                    this.userGroups[idx] = res
                }
                return res
            } catch (err) {
                console.error(err)
                throw err;
            } finally {
                this.loading = false
            }
        },

        async deleteGroup(id: string): Promise<ErrorMessage | void> {
            this.loading = true
            try {
                const res = await deleteGroup(id)

                if (res) {
                    return res
                }

                this.userGroups = this.userGroups.filter(g => g.id !== id)
            } catch (err) {
                console.error(err)
                throw err;
            } finally {
                this.loading = false
            }
        },

        async fetchUserGroupNotifications(id: string): Promise<Record<string, UserGroupNotification>> {
            this.loading = true
            try {
                const res = await fetchUserGroupNotifications(id)

                this.userGroupNotifications[id] = res
                return res
            } catch (err) {
                console.error(err)
                throw err;
            } finally {
                this.loading = false
            }
        },

        async saveUserGroupNotifications(id: string, data: UserGroupNotification[]): Promise<ErrorMessage | void> {
            this.loading = true
            try {
                const res = await changeUserGroupNotifications(id, data)

                if (res) {
                    return res
                }
            } catch (err) {
                console.error(err)
                throw err;
            } finally {
                this.loading = false
            }
        },

        dtoToUserGroup(dto: UserGroupDTO): UserGroup {
            const users: User[] = []

            return {
                id: dto.id,
                name: dto.name,
                description: dto.description,
                users: users,
                enabled: dto.enabled
            }
        },

        userGroupToDto(userGroup: UserGroup): UserGroupDTO {
            return {
                id: userGroup.id,
                name: userGroup.name,
                description: userGroup.description,
                users: userGroup.users.map(u => u.id),
                enabled: userGroup.enabled
            }
        },

        dtoToUserGroupNotification(dto: UserGroupNotificationDTO): UserGroupNotification {
            const resourceType = {} as ResourceType // Placeholder for actual resource type fetching logic

            return {
                resourceType: resourceType,
                notifyOnCreate: dto.notifyOnCreation,
                notifyOnUpdate: dto.notifyOnUpdate,
                notifyOnDelete: dto.notifyOnDeletion,
                enabled: dto.enabled
            }
        },

        userGroupNotificationToDto(notification: UserGroupNotification): UserGroupNotificationDTO {
            return {
                resourceTypeId: notification.resourceType.id,
                notifyOnCreation: notification.notifyOnCreate,
                notifyOnUpdate: notification.notifyOnUpdate,
                notifyOnDeletion: notification.notifyOnDelete,
                enabled: notification.enabled
            }
        }
    }
});
